import React from 'react';
import { Crown, Leaf, Wind, Star, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useContent } from '../context/ContentContext';
import { useModal } from '../context/ModalContext';

const icons = { crown: Crown, leaf: Leaf, wind: Wind, star: Star };

const parseLines = (str) =>
  (str || '').split('\n').map((s) => s.trim()).filter(Boolean);

const ProductsSection = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { siteContent } = useContent();
  const { openModal } = useModal();
  const isRtl = language === 'ar';

  const categories = (siteContent?.products?.categories || [])
    .filter((c) => c.enabled !== false)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const handleProductClick = (cat) => {
    openModal('product', {
      key: cat.key,
      image_url: cat.image_url,
      title: language === 'ar' ? cat.title_ar : cat.title_en,
      content: language === 'ar' ? cat.modal_content_ar : cat.modal_content_en,
      badge: language === 'ar' ? cat.badge_ar : cat.badge_en,
      tagline: language === 'ar' ? cat.tagline_ar : cat.tagline_en,
      features: parseLines(language === 'ar' ? cat.features_ar : cat.features_en),
      variants: parseLines(language === 'ar' ? cat.variants_ar : cat.variants_en),
      origins: parseLines(language === 'ar' ? cat.origins_ar : cat.origins_en),
    });
  };

  const count = categories.length;
  const gridCols =
    count === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
    count === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto' :
    count === 3 ? 'grid-cols-1 sm:grid-cols-3' :
    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  return (
    <section
      id="products"
      data-testid="products-section"
      className={`py-20 sm:py-28 ${isDark ? 'bg-[#050505]' : 'bg-[#FDFBF7]'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Section Header — matches About & Contact exactly ── */}
        <div className="text-center mb-16">
          <p
            className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-4"
            data-testid="products-overline"
          >
            {t('products.overline')}
          </p>
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight"
            data-testid="products-title"
          >
            <span className="gold-text">{t('products.title')}</span>
          </h2>
          <p
            className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}
            data-testid="products-subtitle"
          >
            {t('products.subtitle')}
          </p>
        </div>

        {/* ── Product Cards ── */}
        {categories.length > 0 && (
          <div className={`grid ${gridCols} gap-6 lg:gap-8`}>
            {categories.map((cat, index) => {
              const IconComponent = icons[cat.icon] || Crown;
              const title = language === 'ar' ? cat.title_ar : cat.title_en;
              const description = language === 'ar' ? cat.description_ar : cat.description_en;

              return (
                <div
                  key={cat.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleProductClick(cat)}
                  onKeyDown={(e) => e.key === 'Enter' && handleProductClick(cat)}
                  className={`flex flex-col group relative rounded-sm overflow-hidden cursor-pointer transition-all duration-300
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-primary)]
                    ${isDark
                      ? 'bg-[#111111] border border-[var(--border-color)] hover:border-[var(--gold-primary)]/50'
                      : 'bg-white border border-gray-200 hover:border-[var(--gold-primary)]/50'
                    }`}
                  data-testid={`product-card-${cat.key}`}
                >
                  {/* Large faded index number — matching About cards */}
                  <span
                    className={`absolute top-4 ${isRtl ? 'left-4' : 'right-4'} font-display text-6xl font-bold select-none pointer-events-none transition-colors duration-300
                      ${isDark ? 'text-white/[0.04] group-hover:text-[var(--gold-primary)]/8' : 'text-gray-900/[0.04] group-hover:text-[var(--gold-primary)]/8'}`}
                  >
                    0{index + 1}
                  </span>

                  {/* Product image */}
                  <div className={`relative aspect-[10/9] overflow-hidden ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                    {cat.image_url ? (
                      <img
                        src={cat.image_url}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <IconComponent className="w-16 h-16 text-[var(--gold-primary)]/15" />
                      </div>
                    )}
                    {/* Subtle gold tint on hover */}
                    <div className="absolute inset-0 bg-[var(--gold-primary)]/0 group-hover:bg-[var(--gold-primary)]/5 transition-colors duration-500" />
                  </div>

                  {/* Gold top accent line under image */}
                  <div className={`h-px ${isDark ? 'bg-[var(--border-color)]' : 'bg-gray-100'} group-hover:bg-[var(--gold-primary)]/30 transition-colors duration-300`} />

                  {/* Card content */}
                  <div className={`flex-1 flex flex-col p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
                    {/* Icon — matching About section icon treatment */}
                    <div className={`w-12 h-12 flex items-center justify-center rounded-sm mb-5 transition-colors duration-300
                      ${isDark
                        ? 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'
                        : 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'
                      }`}
                    >
                      <IconComponent className="w-6 h-6 text-[var(--gold-primary)]" />
                    </div>

                    <h3
                      className={`font-display text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {title}
                    </h3>

                    <p
                      className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}
                    >
                      {description}
                    </p>

                    <span
                      className={`inline-flex items-center gap-2 text-[var(--gold-primary)] text-xs font-semibold uppercase tracking-[0.15em] ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                      {t('products.cta')}
                      <ArrowRight
                        className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200
                          ${isRtl ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}
                      />
                    </span>
                  </div>

                  {/* Bottom gold underline — exactly matching About value cards */}
                  <div className="absolute bottom-0 left-0 w-0 h-[3px] bg-[var(--gold-primary)] group-hover:w-full transition-all duration-500" />
                </div>
              );
            })}
          </div>
        )}

        {/* ── Quality Banner — matches Contact section's CTA strip ── */}
        <div
          className={`mt-16 p-8 sm:p-12 rounded-sm relative overflow-hidden
            ${isDark
              ? 'bg-gradient-to-r from-[var(--gold-primary)]/10 via-[var(--gold-primary)]/5 to-[var(--gold-primary)]/10 border border-[var(--gold-primary)]/20'
              : 'bg-gradient-to-r from-[var(--gold-primary)]/5 via-[var(--gold-primary)]/10 to-[var(--gold-primary)]/5 border border-[var(--gold-primary)]/20'
            }`}
        >
          <div className={`relative grid md:grid-cols-2 gap-8 items-center`}>
            <div className={isRtl ? 'text-right' : 'text-left'}>
              <p
                className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-3"
                data-testid="quality-overline"
              >
                {t('quality.overline')}
              </p>
              <h3
                className={`font-display text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}
                data-testid="quality-title"
              >
                {t('quality.title')}
              </h3>
              <p
                className={`leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}
                data-testid="quality-description"
              >
                {t('quality.description')}
              </p>
            </div>
            <div className={`flex items-center justify-center ${isRtl ? 'md:justify-start' : 'md:justify-end'}`}>
              <img
                src={
                  siteContent?.global?.logo_url_dark ||
                  '/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png'
                }
                alt="Royal Tobacco"
                className="h-32 sm:h-40 w-auto opacity-80"
              />
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProductsSection;
